// Copyright 2026 Parity Technologies (UK) Ltd.
// SPDX-License-Identifier: Apache-2.0
import type { AbiEntry, AbiParam } from "./types.js";
import { loadPvmContractAbi } from "./pvm.js";

/** A contract input to {@link generateContractTypes}: either an inline ABI or a `cargo-pvm-contract` artefact path. */
export type ContractTypeInput =
    | { library: string; abi: AbiEntry[] }
    | { library: string; abiPath: string };

/**
 * Resolve a heterogeneous list of {@link ContractTypeInput} entries into the
 * `{ library, abi }` shape consumed by {@link generateContractTypes}. Reads any
 * `abiPath` entries off disk via {@link loadPvmContractAbi} (Node only).
 *
 * @example
 * ```ts
 * const resolved = await resolveContractTypeInputs([
 *     { library: "@example/counter", abiPath: "./target/counter.release.abi.json" },
 *     { library: "@example/inline", abi: counterAbi },
 * ]);
 * const src = generateContractTypes(resolved);
 * ```
 */
export async function resolveContractTypeInputs(
    inputs: readonly ContractTypeInput[],
): Promise<{ library: string; abi: AbiEntry[] }[]> {
    return Promise.all(
        inputs.map(async (input) => {
            if ("abi" in input) return { library: input.library, abi: input.abi };
            return { library: input.library, abi: await loadPvmContractAbi(input.abiPath) };
        }),
    );
}

/** Map a Solidity ABI type to its TypeScript equivalent. */
function mapSolidityType(param: AbiParam): string {
    const t = param.type;

    // Dynamic arrays — e.g. uint256[]
    if (t.endsWith("[]")) {
        const inner = { ...param, type: t.slice(0, -2) };
        return `${mapSolidityType(inner)}[]`;
    }

    // Fixed-size arrays — e.g. uint256[3]
    const fixedArrayMatch = t.match(/^(.+)\[(\d+)\]$/);
    if (fixedArrayMatch) {
        const inner = { ...param, type: fixedArrayMatch[1] };
        return `${mapSolidityType(inner)}[]`;
    }

    // Tuple
    if (t === "tuple" && param.components) {
        const fields = param.components.map((c) => `${c.name}: ${mapSolidityType(c)}`);
        return `{ ${fields.join("; ")} }`;
    }

    // Unsigned integers — uint8/16/32 fit in JS number
    if (/^uint(8|16|32)$/.test(t)) return "number";
    if (/^uint\d*$/.test(t)) return "bigint";

    // Signed integers
    if (/^int(8|16|32)$/.test(t)) return "number";
    if (/^int\d*$/.test(t)) return "bigint";

    // Address
    if (t === "address") return "HexString";

    // Bytes — viem's ABI codec accepts hex strings (`0x…`) for both
    // variable-length `bytes` and fixed-length `bytesN`. We surface
    // `SizedHex<N>` for the latter so consumers see the byte width at
    // compile time, even though the runtime check is just on the prefix.
    if (t === "bytes") return "HexString";
    const bytesMatch = t.match(/^bytes(\d+)$/);
    if (bytesMatch) return `SizedHex<${bytesMatch[1]}>`;

    // Primitives
    if (t === "bool") return "boolean";
    if (t === "string") return "string";

    return "unknown";
}

function generateMethodArgsType(inputs: AbiParam[]): string {
    if (inputs.length === 0) return "[]";
    const parts = inputs.map((p) => `${p.name}: ${mapSolidityType(p)}`);
    return `[${parts.join(", ")}]`;
}

function generateMethodResponseType(outputs: AbiParam[] | undefined): string {
    if (!outputs || outputs.length === 0) return "undefined";
    if (outputs.length === 1) return mapSolidityType(outputs[0]);
    const fields = outputs.map((o, i) => {
        const name = o.name || `_${i}`;
        return `${name}: ${mapSolidityType(o)}`;
    });
    return `{ ${fields.join("; ")} }`;
}

/**
 * Generate a TypeScript module augmentation that extends the
 * {@link Contracts} interface with typed method signatures for each
 * installed contract.
 *
 * The output is written to `.cdm/contracts.d.ts` (or equivalent) and
 * augments `"@parity/product-sdk-contracts"` so that
 * `ContractManager.getContract()` returns fully-typed handles.
 *
 * Accepts `{ library, abi }` directly. To pull ABIs from
 * `cargo-pvm-contract` build artefacts on disk, use
 * {@link resolveContractTypeInputs} first.
 *
 * @example
 * ```ts
 * // Inline ABIs
 * const src = generateContractTypes([
 *     { library: "@example/counter", abi },
 * ]);
 * writeFileSync(".cdm/contracts.d.ts", src);
 *
 * // From cargo-pvm-contract artefacts
 * const resolved = await resolveContractTypeInputs([
 *     { library: "@example/counter", abiPath: "./target/counter.release.abi.json" },
 * ]);
 * writeFileSync(".cdm/contracts.d.ts", generateContractTypes(resolved));
 * ```
 */
export function generateContractTypes(contracts: { library: string; abi: AbiEntry[] }[]): string {
    const lines: string[] = [
        "// Auto-generated by cdm install — do not edit",
        'import type { HexString, SizedHex } from "polkadot-api";',
        "",
        'declare module "@parity/product-sdk-contracts" {',
        "    interface Contracts {",
    ];

    for (const contract of contracts) {
        const methods = contract.abi.filter((e) => e.type === "function" && e.name);
        lines.push(`        "${contract.library}": {`);
        lines.push("            methods: {");
        for (const method of methods) {
            const args = generateMethodArgsType(method.inputs);
            const response = generateMethodResponseType(method.outputs);
            lines.push(
                `                ${method.name!}: { args: ${args}; response: ${response} };`,
            );
        }
        lines.push("            };");
        lines.push("        };");
    }

    lines.push("    }");
    lines.push("}");
    lines.push("");

    return lines.join("\n");
}

if (import.meta.vitest) {
    const { test, expect, describe } = import.meta.vitest;

    describe("mapSolidityType", () => {
        const cases: [AbiParam, string][] = [
            [{ name: "x", type: "uint8" }, "number"],
            [{ name: "x", type: "uint16" }, "number"],
            [{ name: "x", type: "uint32" }, "number"],
            [{ name: "x", type: "uint64" }, "bigint"],
            [{ name: "x", type: "uint128" }, "bigint"],
            [{ name: "x", type: "uint256" }, "bigint"],
            [{ name: "x", type: "int8" }, "number"],
            [{ name: "x", type: "int32" }, "number"],
            [{ name: "x", type: "int64" }, "bigint"],
            [{ name: "x", type: "int256" }, "bigint"],
            [{ name: "x", type: "address" }, "HexString"],
            [{ name: "x", type: "bool" }, "boolean"],
            [{ name: "x", type: "string" }, "string"],
            [{ name: "x", type: "bytes" }, "HexString"],
            [{ name: "x", type: "bytes32" }, "SizedHex<32>"],
            [{ name: "x", type: "bytes4" }, "SizedHex<4>"],
            [{ name: "x", type: "uint256[]" }, "bigint[]"],
            [{ name: "x", type: "address[]" }, "HexString[]"],
            [{ name: "x", type: "uint256[3]" }, "bigint[]"],
            [{ name: "x", type: "somethingWeird" }, "unknown"],
        ];

        for (const [param, expected] of cases) {
            test(`${param.type} → ${expected}`, () => {
                expect(mapSolidityType(param)).toBe(expected);
            });
        }

        test("tuple with components", () => {
            const param: AbiParam = {
                name: "info",
                type: "tuple",
                components: [
                    { name: "addr", type: "address" },
                    { name: "amount", type: "uint256" },
                ],
            };
            expect(mapSolidityType(param)).toBe("{ addr: HexString; amount: bigint }");
        });

        test("nested array of tuples", () => {
            const param: AbiParam = {
                name: "items",
                type: "tuple[]",
                components: [
                    { name: "id", type: "uint32" },
                    { name: "name", type: "string" },
                ],
            };
            expect(mapSolidityType(param)).toBe("{ id: number; name: string }[]");
        });
    });

    describe("generateMethodArgsType", () => {
        test("empty inputs", () => {
            expect(generateMethodArgsType([])).toBe("[]");
        });

        test("single input", () => {
            expect(generateMethodArgsType([{ name: "to", type: "address" }])).toBe(
                "[to: HexString]",
            );
        });

        test("multiple inputs", () => {
            const result = generateMethodArgsType([
                { name: "to", type: "address" },
                { name: "amount", type: "uint256" },
            ]);
            expect(result).toBe("[to: HexString, amount: bigint]");
        });
    });

    describe("generateMethodResponseType", () => {
        test("no outputs", () => {
            expect(generateMethodResponseType(undefined)).toBe("undefined");
            expect(generateMethodResponseType([])).toBe("undefined");
        });

        test("single output", () => {
            expect(generateMethodResponseType([{ name: "", type: "uint32" }])).toBe("number");
        });

        test("multiple outputs", () => {
            const result = generateMethodResponseType([
                { name: "balance", type: "uint256" },
                { name: "nonce", type: "uint32" },
            ]);
            expect(result).toBe("{ balance: bigint; nonce: number }");
        });

        test("unnamed outputs get positional names", () => {
            const result = generateMethodResponseType([
                { name: "", type: "bool" },
                { name: "", type: "uint256" },
            ]);
            expect(result).toBe("{ _0: boolean; _1: bigint }");
        });
    });

    describe("generateContractTypes", () => {
        test("generates valid module augmentation", () => {
            const result = generateContractTypes([
                {
                    library: "@example/counter",
                    abi: [
                        {
                            type: "constructor",
                            inputs: [],
                            stateMutability: "nonpayable",
                        },
                        {
                            type: "function",
                            name: "getCount",
                            inputs: [],
                            outputs: [{ name: "", type: "uint32" }],
                            stateMutability: "view",
                        },
                        {
                            type: "function",
                            name: "increment",
                            inputs: [],
                            outputs: [],
                            stateMutability: "nonpayable",
                        },
                    ],
                },
            ]);

            expect(result).toContain('declare module "@parity/product-sdk-contracts"');
            expect(result).toContain("interface Contracts");
            expect(result).toContain('"@example/counter"');
            expect(result).toContain("getCount: { args: []; response: number };");
            expect(result).toContain("increment: { args: []; response: undefined };");
            // Should not include constructor
            expect(result).not.toContain("constructor");
        });

        test("handles multiple contracts", () => {
            const result = generateContractTypes([
                {
                    library: "@a/one",
                    abi: [
                        {
                            type: "function",
                            name: "foo",
                            inputs: [{ name: "x", type: "uint256" }],
                            outputs: [{ name: "", type: "bool" }],
                        },
                    ],
                },
                {
                    library: "@b/two",
                    abi: [
                        {
                            type: "function",
                            name: "bar",
                            inputs: [],
                            outputs: [{ name: "", type: "address" }],
                        },
                    ],
                },
            ]);

            expect(result).toContain('"@a/one"');
            expect(result).toContain('"@b/two"');
            expect(result).toContain("foo: { args: [x: bigint]; response: boolean };");
            expect(result).toContain("bar: { args: []; response: HexString };");
        });

        test("empty contracts list", () => {
            const result = generateContractTypes([]);
            expect(result).toContain("interface Contracts {");
            expect(result).toContain("}");
        });

        test("includes polkadot-api type imports", () => {
            const result = generateContractTypes([]);
            expect(result).toContain("import type { HexString, SizedHex }");
            expect(result).toContain('from "polkadot-api"');
        });
    });

    describe("resolveContractTypeInputs", () => {
        test("passes inline { library, abi } through unchanged", async () => {
            const abi: AbiEntry[] = [
                {
                    type: "function",
                    name: "foo",
                    inputs: [],
                    outputs: [{ name: "", type: "bool" }],
                },
            ];
            const out = await resolveContractTypeInputs([{ library: "@x/foo", abi }]);
            expect(out).toEqual([{ library: "@x/foo", abi }]);
        });

        test("loads ABI from { abiPath } on disk", async () => {
            const fs = await import("node:fs/promises");
            const path = await import("node:path");
            const os = await import("node:os");
            const tmp = await fs.mkdtemp(path.join(os.tmpdir(), "codegen-"));
            const abi: AbiEntry[] = [
                {
                    type: "function",
                    name: "ping",
                    inputs: [],
                    outputs: [{ name: "", type: "bool" }],
                },
            ];
            const file = path.join(tmp, "ping.release.abi.json");
            await fs.writeFile(file, JSON.stringify(abi), "utf8");
            try {
                const out = await resolveContractTypeInputs([
                    { library: "@x/ping", abiPath: file },
                ]);
                expect(out).toEqual([{ library: "@x/ping", abi }]);

                // And it composes with generateContractTypes end-to-end:
                const src = generateContractTypes(out);
                expect(src).toContain('"@x/ping"');
                expect(src).toContain("ping: { args: []; response: boolean };");
            } finally {
                await fs.rm(tmp, { recursive: true, force: true });
            }
        });
    });
}
